import { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  Clock,
  Calendar,
  User,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  HelpCircle,
  ChevronDown,
  Sparkles,
  Share2,
  PackageCheck
} from 'lucide-react';
import { SEO } from '../../shared/components/SEO';
import { getBlogPostBySlug, blogPosts } from '../../shared/data/blogData';

export function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlug(slug) : undefined;
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [copied, setCopied] = useState(false);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const otherPosts = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.summary,
          url
        });
      } catch {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <article className="min-h-screen bg-page">
      <SEO
        title={post.metaTitle || `${post.title} | ZFAST Agritech`}
        description={post.metaDescription || post.summary}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.coverImage}
        faqs={post.faqs}
        breadcrumbs={[
          { label: 'Knowledge Hub', href: '/blog' },
          { label: post.title, href: `/blog/${post.slug}` }
        ]}
        article={{
          publishedTime: post.publishedAt,
          modifiedTime: post.updatedAt,
          author: post.author.name,
          section: post.category,
          tags: post.keywords
        }}
      />

      {/* Header Banner */}
      <header className="relative bg-forest py-12 text-white md:py-16">
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb Navigation */}
          <nav className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-wider text-tint" aria-label="Breadcrumbs">
            <Link to="/" className="hover:underline">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/blog" className="hover:underline">Knowledge Hub</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white/70 truncate max-w-[200px] sm:max-w-xs">{post.category}</span>
          </nav>

          <div className="mt-4">
            <span className="inline-block rounded-full bg-white/15 px-3 py-1 text-xs font-black uppercase tracking-widest text-[#DDEFCB]">
              {post.category}
            </span>
            <h1 className="mt-4 text-2xl font-black leading-tight text-white sm:text-3xl md:text-4xl lg:text-5xl">
              {post.title}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/85 sm:text-lg">
              {post.summary}
            </p>

            {/* Author and Meta Bar */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-white/15 pt-5 text-xs text-white/80">
              <div className="flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-1.5 font-bold">
                  <User className="h-4 w-4 text-[#CFE8B7]" />
                  {post.author.name} ({post.author.role})
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 text-[#CFE8B7]" />
                  {new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-[#CFE8B7]" />
                  {post.readTime}
                </span>
              </div>

              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3.5 py-1.5 font-bold text-white transition hover:bg-white/20"
                aria-label="Share article"
              >
                <Share2 className="h-3.5 w-3.5" />
                <span>{copied ? 'Link Copied!' : 'Share'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="mx-auto max-w-[1200px] px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1fr_340px]">
          {/* Main Article Body */}
          <div className="min-w-0">
            {/* Featured Image */}
            <div className="overflow-hidden rounded-3xl border border-forest/10 bg-white shadow-soft">
              <img
                src={post.coverImage}
                alt={post.title}
                className="h-auto max-h-[460px] w-full object-cover"
              />
            </div>

            {/* Key Takeaways Box */}
            {post.keyTakeaways && post.keyTakeaways.length > 0 && (
              <div className="mt-8 rounded-3xl border border-[#7CB342]/30 bg-[#F1F8E9] p-6 sm:p-8">
                <div className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-forest">
                  <Sparkles className="h-5 w-5 text-[#689F38]" />
                  Key Takeaways & Summary
                </div>
                <ul className="mt-4 grid gap-3">
                  {post.keyTakeaways.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm font-semibold text-charcoal/90 leading-relaxed">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-[#689F38] mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Article Sections */}
            <div className="mt-10 space-y-10">
              {post.sections.map((section, idx) => (
                <section key={idx} className="scroll-mt-24">
                  <h2 className="text-xl font-black tracking-tight text-ink sm:text-2xl md:text-3xl">
                    {section.heading}
                  </h2>

                  {section.subheading && (
                    <h3 className="mt-2 text-base font-bold text-forest sm:text-lg">
                      {section.subheading}
                    </h3>
                  )}

                  <div className="mt-4 space-y-4 text-base leading-relaxed text-charcoal/85">
                    {section.content.map((paragraph, pIdx) => (
                      <p key={pIdx}>{paragraph}</p>
                    ))}
                  </div>

                  {section.bulletPoints && section.bulletPoints.length > 0 && (
                    <ul className="mt-4 space-y-2.5 rounded-2xl bg-white p-5 border border-forest/10">
                      {section.bulletPoints.map((point, bIdx) => (
                        <li key={bIdx} className="flex items-start gap-3 text-sm font-semibold text-charcoal">
                          <span className="h-2 w-2 rounded-full bg-forest mt-2 shrink-0" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.callout && (
                    <div className="mt-6 rounded-2xl border-l-4 border-forest bg-tint/60 p-5">
                      <p className="text-xs font-black uppercase tracking-wider text-forest">
                        {section.callout.title}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-charcoal leading-relaxed">
                        {section.callout.text}
                      </p>
                    </div>
                  )}
                </section>
              ))}
            </div>

            {/* In-Article Related Products Section */}
            {post.relatedProducts && post.relatedProducts.length > 0 && (
              <div className="mt-14 rounded-3xl border border-forest/15 bg-white p-6 sm:p-8 shadow-soft">
                <div className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-forest">
                  <PackageCheck className="h-5 w-5 text-forest" />
                  Recommended ZFAST Products for This Guide
                </div>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {post.relatedProducts.map((prod, idx) => (
                    <div key={idx} className="flex flex-col justify-between rounded-2xl bg-tint/40 p-5 border border-forest/10">
                      <div>
                        <span className="text-[11px] font-black uppercase tracking-wide text-forest/70">
                          {prod.category}
                        </span>
                        <h4 className="mt-1 text-base font-black text-ink">
                          {prod.name}
                        </h4>
                        <p className="mt-1.5 text-xs text-ink/70 leading-relaxed">
                          {prod.tagline}
                        </p>
                      </div>
                      <Link
                        to={prod.href}
                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-black text-forest hover:underline"
                      >
                        View Product Details
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Frequently Asked Questions Section */}
            {post.faqs && post.faqs.length > 0 && (
              <section className="mt-14 rounded-3xl border border-forest/10 bg-white p-6 sm:p-8 shadow-soft">
                <div className="flex items-center gap-2 text-sm font-black uppercase tracking-wider text-forest">
                  <HelpCircle className="h-5 w-5 text-forest" />
                  Frequently Asked Questions
                </div>
                <h3 className="mt-2 text-xl font-black text-ink">
                  Common Questions on This Topic
                </h3>

                <div className="mt-6 divide-y divide-forest/10">
                  {post.faqs.map((faq, fIdx) => (
                    <div key={fIdx} className="py-4">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(openFaq === fIdx ? null : fIdx)}
                        className="flex w-full items-center justify-between gap-4 text-left text-base font-bold text-ink hover:text-forest"
                        aria-expanded={openFaq === fIdx}
                      >
                        <span>{faq.question}</span>
                        <ChevronDown className={`h-5 w-5 shrink-0 text-forest transition-transform ${openFaq === fIdx ? 'rotate-180' : ''}`} />
                      </button>
                      {openFaq === fIdx && (
                        <p className="mt-3 text-sm leading-relaxed text-ink/75">
                          {faq.answer}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Author Profile */}
            <div className="mt-12 flex flex-col items-start gap-4 rounded-3xl border border-forest/10 bg-white p-6 shadow-soft sm:flex-row sm:items-center sm:gap-6">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-forest text-white font-black text-xl">
                Z
              </div>
              <div className="flex-1">
                <p className="text-xs font-black uppercase tracking-wider text-forest/70">Published By</p>
                <h4 className="text-base font-black text-ink">{post.author.name}</h4>
                <p className="text-xs font-semibold text-ink/60">{post.author.role}</p>
                <p className="mt-2 text-xs leading-relaxed text-ink/75">
                  ZFAST Agritech India is a premier manufacturer in Jaipur, Rajasthan, dedicated to scientific animal nutrition, aflatoxin-controlled feed production, and wholesale edible oil supply.
                </p>
              </div>
            </div>

            {/* Back to Blog Button */}
            <div className="mt-10">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-white px-5 py-2.5 text-xs font-black text-forest shadow-sm transition hover:bg-forest hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to All Articles
              </Link>
            </div>
          </div>

          {/* Sidebar Area */}
          <aside className="space-y-8">
            {/* Quick Summary Card */}
            <div className="rounded-3xl border border-forest/10 bg-white p-6 shadow-soft">
              <h3 className="text-sm font-black uppercase tracking-wider text-forest">Article Summary</h3>
              <p className="mt-3 text-xs leading-relaxed text-ink/75">
                {post.summary}
              </p>
              <div className="mt-4 pt-4 border-t border-forest/10 space-y-2 text-xs">
                <div className="flex justify-between text-ink/70">
                  <span className="font-semibold">Category:</span>
                  <span className="font-bold text-forest">{post.category}</span>
                </div>
                <div className="flex justify-between text-ink/70">
                  <span className="font-semibold">Read Time:</span>
                  <span className="font-bold">{post.readTime}</span>
                </div>
                <div className="flex justify-between text-ink/70">
                  <span className="font-semibold">Updated:</span>
                  <span className="font-bold">{post.updatedAt}</span>
                </div>
              </div>
            </div>

            {/* Dealership / Contact CTA */}
            <div className="rounded-3xl bg-forest p-6 text-white shadow-soft">
              <span className="text-[11px] font-black uppercase tracking-widest text-[#DDEFCB]">Wholesale & Dealership</span>
              <h3 className="mt-2 text-lg font-black leading-snug text-white">
                Interested in ZFAST Feeds or Edible Oils?
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-white/80">
                Contact our Jaipur sales desk for dealership inquiries, bulk orders, and technical feed consultation.
              </p>
              <div className="mt-5 space-y-2.5">
                <Link
                  to="/dealer-partnership"
                  className="block w-full rounded-2xl bg-[#7CB342] py-2.5 text-center text-xs font-black text-white hover:bg-[#689F38] transition"
                >
                  Become a Dealer
                </Link>
                <Link
                  to="/contact"
                  className="block w-full rounded-2xl bg-white/10 border border-white/20 py-2.5 text-center text-xs font-black text-white hover:bg-white/20 transition"
                >
                  Contact Jaipur Office
                </Link>
              </div>
            </div>

            {/* Related Articles */}
            {otherPosts.length > 0 && (
              <div className="rounded-3xl border border-forest/10 bg-white p-6 shadow-soft">
                <h3 className="text-sm font-black uppercase tracking-wider text-forest">Related Guides</h3>
                <div className="mt-4 space-y-4">
                  {otherPosts.map((op) => (
                    <Link
                      key={op.slug}
                      to={`/blog/${op.slug}`}
                      className="group block border-b border-forest/10 pb-4 last:border-0 last:pb-0"
                    >
                      <span className="text-[10px] font-bold text-forest/70 uppercase">{op.category}</span>
                      <h4 className="mt-1 text-xs font-bold leading-snug text-ink group-hover:text-forest transition line-clamp-2">
                        {op.title}
                      </h4>
                      <p className="mt-1 text-[11px] text-ink/50">{op.readTime}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>
    </article>
  );
}
